<script lang="ts">
  import ProgressStepsContent from "@kontextwerk/web-sdk/ui/navigation/ProgressStepsContent.svelte"
  import { onDestroy, onMount, untrack } from "svelte"
  import { newNotification } from "@kontextwerk/web-sdk/core/overlay/notifications"
  import ProgressSteps, {
    type ProgressStep,
  } from "@kontextwerk/web-sdk/ui/navigation/ProgressSteps.svelte"
  import type { ProgressStepContext } from "@kontextwerk/web-sdk/core/navigation/progressSteps"
  import {
    firstCurrentWorkflowStep,
    LOCKED_WORKFLOW_STEP_MESSAGE,
    selectedWorkflowStepIndex,
    workflowStateForStep,
  } from "../../../../../_shared/_logic/workflow-progress"
  import type {
    CancelReplanInit,
    CancelReplanMode,
    CancelReplanStepId,
    CancelReplanWorkflowEvent,
    CancelReplanWorkflowOutputs,
    EmployeeOption,
    OverlayHostProps,
  } from "../../_logic/cancel-replan-contract"
  import { buildCancelReplanProgressEntries, stepOrderForCancelReplanMode } from "./_logic/progress"
  import { applyCancelReplanWorkflowEvent, nextCancelReplanStep } from "./_logic/transitions"
  import { buildActiveContactOrderByWorkOrderId } from "../../_logic/rows"
  import CancelEventsStep from "./steps/a_cancel_events"
  import PlanReplacementsStep from "./steps/c_plan_replacements"
  import AnnounceReplacementsStep, {
    createContactOrderPlanningDraft,
  } from "./steps/d_announce_replacements"
  import CancelAnnouncementPlansStep from "./steps/e_cancel_announcement_plans"
  import SummaryStep from "./steps/f_summary"
  import type { WorkOrderRow } from "../../_logic/work-order-table-model"
  import type { PlanningUnitContactContext } from "../../../../../../../api/kehrblatt/planning"
  import {
    loadMissingPlanningUnitContactContexts,
    planningUnitContactTargetsForWorkOrders,
  } from "../../../../../_shared/_logic/planning-contact-contexts"

  let {
    organizationId,
    mode = "rescheduleAppointments",
    events = [],
    workOrders = [],
    sourceRows = [],
    properties = [],
    customers = [],
    contactPoints = [],
    contactOrders = [],
    employees = [],
    workingHoursByUser = {},
    startDate = null,
    planningSettings = null,
    trialRestrictionsActive = false,
    onCompleted = async () => {},
    onremoveOverlay = () => {},
    onshowOverlay = null,
    setActionButtonSnippet = null,
  }: {
    organizationId: string
    mode?: CancelReplanMode
    events?: ScheduleEvent[]
    workOrders?: WorkOrder[]
    sourceRows?: WorkOrderRow[]
    properties?: Property[]
    customers?: Customer[]
    contactPoints?: ContactPoint[]
    contactOrders?: ContactOrder[]
    employees?: EmployeeOption[]
    workingHoursByUser?: CancelReplanInit["workingHoursByUser"]
    startDate?: string | null
    planningSettings?: CancelReplanInit["planningSettings"]
    trialRestrictionsActive?: boolean | null
    onCompleted?: () => void | Promise<void>
  } & OverlayHostProps = $props()

  const initialMode = untrack(() => mode ?? "rescheduleAppointments")
  const contactPlanningDraft = untrack(() => createContactOrderPlanningDraft(organizationId))
  const hasActiveAnnouncementPlan = (orders: WorkOrder[], activeOrders: ContactOrder[]) => {
    const activeContactOrderByWorkOrderId = buildActiveContactOrderByWorkOrderId(activeOrders)

    return orders.some((order) => Boolean(order.id && activeContactOrderByWorkOrderId[order.id]))
  }
  const initialIncludeCancelAnnouncementsStep = untrack(
    () =>
      initialMode !== "createAnnouncementPlans" ||
      hasActiveAnnouncementPlan(workOrders, contactOrders),
  )
  let unitContactContexts = $state<PlanningUnitContactContext[]>([])
  let contactContextRequestId = 0
  const unitContactTargets = $derived(planningUnitContactTargetsForWorkOrders(workOrders))
  $effect(() => {
    const targets = unitContactTargets
    const existing = untrack(() => unitContactContexts)

    if (!organizationId || !targets.length) return

    const requestId = ++contactContextRequestId
    void loadMissingPlanningUnitContactContexts({
      organizationId,
      targets,
      existing,
    })
      .then((contexts) => {
        if (requestId === contactContextRequestId) unitContactContexts = contexts
      })
      .catch(() => {
        console.warn("cancel_replan_contact_context_load_failed", {
          target_count: targets.length,
        })
      })
  })
  const init: CancelReplanInit = $derived({
    organizationId,
    mode,
    events,
    workOrders,
    sourceRows,
    properties,
    customers,
    contactPoints,
    contactOrders,
    unitContactContexts,
    employees,
    workingHoursByUser,
    startDate,
    planningSettings,
    trialRestrictionsActive,
  })
  const activeMode: CancelReplanMode = $derived(mode ?? "rescheduleAppointments")
  const includeCancelAnnouncementsStep = $derived(
    activeMode !== "createAnnouncementPlans" ||
      hasActiveAnnouncementPlan(workOrders, contactOrders),
  )
  const stepOrderOptions = $derived({ includeCancelAnnouncementsStep })

  let selectedStep = $state<CancelReplanStepId>(
    stepOrderForCancelReplanMode(initialMode, {
      includeCancelAnnouncementsStep: initialIncludeCancelAnnouncementsStep,
    })[0] ?? "cancel",
  )
  let outputs = $state<CancelReplanWorkflowOutputs>({})
  let stepError = $state<string | null>(null)
  let finishing = $state(false)
  let mutatedSinceOpen = false
  let completedInvalidation = false
  let closeInvalidationPromise: Promise<void> | null = null

  const setStep = (step: CancelReplanStepId) => {
    stepError = null
    selectedStep = step
  }

  const setStepError = (message: string | null) => {
    stepError = message
  }

  const hasMutated = (event: CancelReplanWorkflowEvent): boolean => {
    if (event.type === "cancel.completed") return event.output.rows.length > 0

    if (event.type === "planning.committed") return event.output.committed

    if (event.type === "cancelAnnouncements.completed") {
      return (
        event.output.action !== "none" &&
        ((event.output.canceled?.length ?? 0) > 0 || (event.output.completed?.length ?? 0) > 0)
      )
    }

    if (event.type === "announce.completed") return (event.output.result.created?.length ?? 0) > 0

    return false
  }

  const markMutated = () => {
    mutatedSinceOpen = true
    completedInvalidation = false
    closeInvalidationPromise = null
  }

  const completeParentInvalidation = async () => {
    await onCompleted()
    completedInvalidation = true
  }

  const invalidateAfterClose = async () => {
    if (!mutatedSinceOpen || completedInvalidation) return

    if (!closeInvalidationPromise) {
      closeInvalidationPromise = completeParentInvalidation().catch((err) => {
        closeInvalidationPromise = null
        throw err
      })
    }

    await closeInvalidationPromise
  }

  onMount(() => {
    onshowOverlay?.()
    setActionButtonSnippet?.(null)

    return () => setActionButtonSnippet?.(null)
  })

  onDestroy(() => {
    if (mutatedSinceOpen && !completedInvalidation) {
      void invalidateAfterClose().catch((err) => {
        console.error("cancel_replan_close_invalidation_failed", err)
      })
    }
  })

  async function applyWorkflowEvent(event: CancelReplanWorkflowEvent) {
    if (event.type === "summary.finished") {
      if (finishing) return

      finishing = true
      try {
        await completeParentInvalidation()
        await onremoveOverlay?.()
      } finally {
        finishing = false
      }

      return
    }

    if (hasMutated(event)) {
      markMutated()
    }

    const nextOutputs = applyCancelReplanWorkflowEvent(outputs, event)
    outputs = nextOutputs
    setStep(
      nextCancelReplanStep({ currentStep: currentStep ?? selectedStep, event, mode: activeMode }),
    )

    if (event.type === "announce.completed") {
      const label =
        activeMode === "rescheduleAppointments"
          ? "Kundeninformationen vorbereitet"
          : "Ankündigungspläne angelegt"
      newNotification({
        class: event.output.result.failed?.length ? "warning" : "success",
        html: `${event.output.result.created?.length ?? 0} ${label}.`,
      })
    }

    if (
      event.type === "cancelAnnouncements.completed" &&
      activeMode === "cancelAnnouncementPlans"
    ) {
      const count =
        event.output.action === "completed"
          ? (event.output.completed?.length ?? 0)
          : (event.output.canceled?.length ?? 0)
      const verb =
        event.output.action === "completed"
          ? "abgeschlossen"
          : event.output.action === "canceled"
            ? "abgebrochen"
            : "beendet"
      newNotification({
        class: event.output.failed?.length ? "warning" : "success",
        html: `${count} Ankündigungspläne ${verb}.`,
      })
    }
  }

  const stepOrder = $derived(stepOrderForCancelReplanMode(activeMode, stepOrderOptions))
  const completedSteps = $derived.by(() => {
    const completed: CancelReplanStepId[] = []

    if (outputs.cancel) completed.push("cancel")

    if (outputs.planning?.committed) completed.push("plan")

    if (outputs.cancelAnnouncements) completed.push("cancelAnnouncements")

    if (outputs.announce) completed.push("announce")

    if (outputs.summary) completed.push("summary")

    return completed.filter((step) => stepOrder.includes(step))
  })
  const currentStep = $derived(
    firstCurrentWorkflowStep<CancelReplanStepId>({
      stepOrder,
      completedSteps,
      selectedStep,
    }),
  )
  const selectedIndex = $derived(
    selectedWorkflowStepIndex<CancelReplanStepId>({
      stepOrder,
      completedSteps,
      selectedStep,
    }),
  )
  const handleSelectedIndexChange = (index: number) => {
    const next = stepOrder[index]

    if (next) setStep(next)
  }

  const progressEntries = $derived(
    buildCancelReplanProgressEntries(outputs, activeMode, stepOrderOptions),
  )

  const announceBackHandler: (() => void) | undefined = $derived.by(() => {
    if (activeMode !== "createAnnouncementPlans") return () => setStep("plan")

    if (includeCancelAnnouncementsStep) return () => setStep("cancelAnnouncements")

    return undefined
  })

  const stepSnippet = (step: CancelReplanStepId) => {
    if (step === "cancel") return cancelStep

    if (step === "plan") return planStep

    if (step === "cancelAnnouncements") return cancelAnnouncementsStep

    if (step === "announce") return announceStep

    return summaryStep
  }

  const steps: ProgressStep[] = $derived(
    progressEntries.map((entry) => ({
      ...entry,
      workflowState: workflowStateForStep({
        step: entry.id,
        currentStep,
        completedSteps,
      }),
      snippet: stepSnippet(entry.id),
    })),
  )
</script>

{#snippet cancelStep(ctx: ProgressStepContext)}
  <CancelEventsStep
    {init}
    output={outputs.cancel}
    readOnly={ctx.readOnly}
    onEvent={applyWorkflowEvent}
    onError={setStepError}
  />
{/snippet}

{#snippet planStep(ctx: ProgressStepContext)}
  {#if outputs.cancel || activeMode === "createAppointments"}
    <PlanReplacementsStep
      {init}
      mode={activeMode}
      cancelOutput={outputs.cancel}
      output={outputs.planning}
      readOnly={ctx.readOnly}
      onEvent={applyWorkflowEvent}
      onError={setStepError}
    />
  {/if}
{/snippet}

{#snippet cancelAnnouncementsStep(ctx: ProgressStepContext)}
  <CancelAnnouncementPlansStep
    {init}
    mode={activeMode}
    output={outputs.cancelAnnouncements}
    readOnly={ctx.readOnly}
    onEvent={applyWorkflowEvent}
    onError={setStepError}
  />
{/snippet}

{#snippet announceStep(ctx: ProgressStepContext)}
  {#if outputs.planning || activeMode === "createAnnouncementPlans"}
    <AnnounceReplacementsStep
      planningDraft={contactPlanningDraft}
      {init}
      mode={activeMode}
      planningOutput={outputs.planning}
      output={outputs.announce}
      readOnly={ctx.readOnly}
      onBack={announceBackHandler}
      onEvent={applyWorkflowEvent}
      onError={setStepError}
    />
  {/if}
{/snippet}

{#snippet summaryStep(ctx: ProgressStepContext)}
  <SummaryStep
    mode={activeMode}
    {outputs}
    readOnly={ctx.readOnly}
    {finishing}
    onEvent={applyWorkflowEvent}
  />
{/snippet}

<div class="cancel-replan-overlay form light">
  <ProgressSteps
    {steps}
    showControls={false}
    darkMode={true}
    syncUrl={false}
    statusMode="explicit"
    navigationMode="guarded"
    lockedStepMessage={LOCKED_WORKFLOW_STEP_MESSAGE}
    {selectedIndex}
    onSelectedIndexChange={handleSelectedIndexChange}
  />
  {#if stepError}
    <ProgressStepsContent>
      <div class="step-error">{stepError}</div>
    </ProgressStepsContent>
  {/if}
</div>

<style lang="less">
  .cancel-replan-overlay {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    min-height: 34rem;
    padding-bottom: 1rem;
  }

  .step-error {
    padding: 0.65rem 0.8rem;
    border: 1px solid color-mix(in srgb, var(--error-100) 40%, transparent);
    background: color-mix(in srgb, var(--error-100) 8%, transparent);
    color: var(--error-100);
    font-weight: 800;
  }
</style>
